import { computed, watch } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import {
  getModalRegistration,
  mergeModalEntries,
  modalsParamToStackEntries,
  parseModalsParam,
  serializeModals,
  stackToModalsParam,
  type ModalStack
} from '@gat-ship/vue-components'
import {
  hasLegacyModalParams,
  legacyModalEntries,
  LEGACY_MODAL_PARAM_KEYS
} from './legacy-redirect'

/**
 * The single owner of the `?modals=` query param (central-modal-host plan,
 * Phase 2). It:
 *
 *  - reads `?modals=` once, in array order, into the stack (z by index — the
 *    deterministic-restore guarantee), and tracks back/forward,
 *  - folds any legacy per-feature params (`?stakeholder=`, `?admin=`, …) into
 *    `?modals=` and strips them — both a deep-link redirect (non-negotiable #4)
 *    and a continuous bridge so existing openers keep working,
 *  - writes the stack back as the *only* writer.
 *
 * Loop-safe: every branch compares against the current `?modals=` string and
 * skips no-op writes. Non-modal params (including the admin sub-scopes
 * `?ports-agency` / `?nodeId`) are preserved.
 */
function queryModals(query: LocationQuery): string {
  const value = query.modals
  const single = Array.isArray(value) ? value[0] : value
  return single ?? ''
}

/**
 * Ephemeral (`persist: false`) kinds participate in the stack/tabs/z-order but
 * are never serialized to `?modals=` and never restored from it (Phase 6.2), so
 * a confirm/picker doesn't resurrect on refresh. Unregistered kinds are treated
 * as persistent (the host simply won't render them).
 */
function isPersistent(kind: string): boolean {
  return getModalRegistration(kind)?.persist !== false
}

export function useModalsUrlSync(stack: ModalStack): void {
  const route = useRoute()
  const router = useRouter()

  // Stack → `?modals=` string (persistent kinds only; ephemeral skipped).
  const serialized = computed(() => stackToModalsParam(stack.entries.value))

  function setStackFromModals(modals: string): void {
    const persistent = modalsParamToStackEntries(modals)
    // Preserve any open ephemeral (persist:false) entries — they aren't in the
    // URL, so a URL-driven set must not drop them; keep them on top.
    const ephemeral = stack.entries.value
      .filter((entry) => !isPersistent(entry.kind))
      .map((entry) => ({ kind: entry.kind, params: entry.params }))
    stack.set([...persistent, ...ephemeral])
  }

  function stripLegacy(query: LocationQuery): LocationQuery {
    const next: LocationQuery = { ...query }
    for (const key of LEGACY_MODAL_PARAM_KEYS) delete next[key]
    return next
  }

  // Read + fold: drive the stack from the URL, folding legacy params in.
  watch(
    () => route.fullPath,
    () => {
      // Fold legacy params into `?modals=` and normalise the URL first.
      if (hasLegacyModalParams(route.query)) {
        const merged = serializeModals(
          mergeModalEntries(
            parseModalsParam(queryModals(route.query)),
            legacyModalEntries(route.query)
          )
        )
        const query = stripLegacy(route.query)
        if (merged === '') delete query.modals
        else query.modals = merged
        void router.replace({ query })
        return
      }
      // Clean URL: drive the stack from `?modals=`.
      const modals = queryModals(route.query)
      if (modals !== serialized.value) setStackFromModals(modals)
    },
    { immediate: true }
  )

  // Write: stack → `?modals=`, as the only writer; skip no-op writes. When a
  // kind leaves the stack, strip its `cleanupQueryKeys` (panel sub-scopes) in
  // the same replace so a sub-scope never lingers without its modal.
  const persistentKinds = () =>
    stack.entries.value
      .filter((entry) => isPersistent(entry.kind))
      .map((entry) => entry.kind)
  let previousKinds = persistentKinds()
  watch(serialized, (next) => {
    const currentKinds = persistentKinds()
    const removed = previousKinds.filter((kind) => !currentKinds.includes(kind))
    const previousLength = previousKinds.length
    previousKinds = currentKinds
    if (next === queryModals(route.query) && removed.length === 0) return

    const query: LocationQuery = { ...route.query }
    if (next === '') delete query.modals
    else query.modals = next
    for (const kind of removed) {
      for (const key of getModalRegistration(kind)?.cleanupQueryKeys ?? []) {
        delete query[key]
      }
    }

    // Trello-like history (decision 0.2), behind this single seam so it can be
    // flipped to replace-only later: opening a modal (the stack grows) pushes a
    // history entry, so `back` closes the top modal; param changes / closes /
    // raises replace, to avoid spamming history.
    if (currentKinds.length > previousLength) void router.push({ query })
    else void router.replace({ query })
  })
}
